import React, { useMemo, useState } from 'react';
import { BiArrowBack, BiCheck } from 'react-icons/bi';
import { getLanguageFromUrl, useTranslation, translate } from '../utils/languageUtils';
import { getThemeIndexFromUrl } from '../utils/urlUtils';
import { getGlobalSelectedOptions } from '../utils/globalOptions';
import { filterViewsByLinkedView } from '../utils/productOptionsUtils';

// Centralized header for navigation + view controls
// Props:
// - configData
// - selectedProduct
// - selectedView
// - setSelectedView(viewObj)
// - onShowThemes()
// - onDone()
// - showViewButtons (boolean) => center shows view pills when true, else step indicator
const HeaderBar = ({
  configData,
  selectedProduct,
  selectedView,
  setSelectedView,
  onShowThemes,
  onDone,
  showViewButtons = true,
}) => {
  const { t } = useTranslation();
  const getViewId = (view) => {
    if (!view) return '';
    if (typeof view === 'string') return view;
    if (typeof view === 'object' && view.id) return view.id;
    return '';
  };

  const getViewName = (view) => {
    if (!view) return '';
    if (typeof view === 'string') return view;
    if (typeof view === 'object') {
      if (view.name && typeof view.name === 'object') {
        const lang = getLanguageFromUrl();
        return view.name[lang] || view.name.en || view.name.cn || view.id || '';
      }
      if (typeof view.name === 'string') return view.name;
      if (view.id) return view.id;
    }
    return '';
  };

  // Bump when global selectedOptions changes (must be declared BEFORE hooks that depend on it)
  const [optionsVersion, setOptionsVersion] = React.useState(0);
  React.useEffect(() => {
    const handler = () => setOptionsVersion(v => v + 1);
    window.addEventListener('globalOptionsChanged', handler);
    // Force one recompute after mount in case the initial event was dispatched before listener attached
    setOptionsVersion(v => v + 1);
    return () => window.removeEventListener('globalOptionsChanged', handler);
  }, []);

  const productViewsRaw = useMemo(() => {
    const v = selectedProduct?.views || selectedProduct?.Views || [];
    return Array.isArray(v) ? v : [];
  }, [selectedProduct]);

  // Recompute when options change to reflect linked_view filtering
  const productViews = useMemo(() => {
    try {
      const optionsCfg = configData?.SupportedModels?.Options;
      const selectedOptions = getGlobalSelectedOptions?.() || {};
      return filterViewsByLinkedView(optionsCfg, selectedOptions, productViewsRaw);
    } catch {
      return productViewsRaw;
    }
  }, [productViewsRaw, configData, optionsVersion]);

  const currentViewId = getViewId(selectedView);
  const rawIndex = productViews.findIndex(v => getViewId(v) === currentViewId);
  const currentIndex = rawIndex < 0 ? 0 : rawIndex;
  const total = productViews.length;
   
  
  // Identify active variant by matching only options flagged as SKU (isSKU: true)
  const activeVariantKey = useMemo(() => {
    try {
      const opts = getGlobalSelectedOptions?.() || {};
      const optionsCfg = configData?.SupportedModels?.Options || {};
      // Determine which option keys define SKU
      let skuKeys = Object.keys(optionsCfg).filter(k => optionsCfg[k]?.isSKU);
      // Fallback: infer SKU keys from variants if no flags are present
      const variants = configData?.variants || configData?.Variants || [];
      if ((!skuKeys || skuKeys.length === 0) && Array.isArray(variants) && variants.length > 0) {
        const allKeys = Object.keys(variants[0] || {});
        const optionKeys = allKeys.filter(k => /^Option\d+$/i.test(k));
        const distinctMap = {};
        for (const k of optionKeys) {
          const vals = new Set(
            variants
              .map(v => v?.[k])
              .filter(v => v !== undefined && v !== null && String(v).trim() !== '')
          );
          if (vals.size > 1) distinctMap[k] = true;
        }
        skuKeys = optionKeys.filter(k => distinctMap[k]);
        if (skuKeys.length === 0) {
          skuKeys = optionKeys.filter(k => /^(Option1|Option2)$/i.test(k));
        }
      }

      // Helper to normalize selected value
      const getSelectedValue = (key) => {
        const v = opts?.[key];
        if (v && typeof v === 'object' && 'value' in v) return v.value;
        return v ?? opts?.[key?.toLowerCase?.()] ?? '';
      };

      // Build selected map only for SKU keys
      const selectedSku = (skuKeys || []).reduce((acc, key) => {
        const val = getSelectedValue(key);
        if (val !== undefined && val !== null && val !== '') acc[key] = val;
        return acc;
      }, {});

      const variant = variants.find(v => {
        // All specified SKU options must match exactly
        for (const key of skuKeys) {
          const need = selectedSku[key];
          if (need === undefined) continue; // ignore unselected SKU option
          const vv = v?.[key] ?? v?.[key?.toLowerCase?.()];
          if (vv !== need) return false;
        }
        return true;
      });
      if (!variant) {
        alert('Active variant not found');
        return false;
      }
      return variant;
    } catch (_) {
      return false;
    }
  }, [configData, optionsVersion]);

  // Expose active variant globally and notify listeners
  React.useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.activeVariant = activeVariantKey || null;
      window.activeVariantId = (activeVariantKey && activeVariantKey.id) ? activeVariantKey.id : null;
      const evt = new CustomEvent('activeVariantChanged', { detail: { variant: activeVariantKey || null } });
      window.dispatchEvent(evt);
    } catch (_) {}
  }, [activeVariantKey]);

  // Resolve limits from ACTIVE VARIANT's DesignProduct for this view
  const maxDesignCount = useMemo(() => {
    try {
      const variant = activeVariantKey;
      const dp = variant?.DesignProduct?.[currentViewId];
      if (!dp) return undefined;
      if (typeof dp === 'string') return undefined; // string template -> unlimited
      const max = typeof dp.maxDesignCount === 'number' ? dp.maxDesignCount : undefined;
      return max;
    } catch (_) {
      return undefined;
    }
  }, [configData, currentViewId, optionsVersion]);

  // Allowed indices from allowDesign_index for this view/variant (controls header range)
  const allowedIndices = useMemo(() => {
    try {
      const dp = activeVariantKey?.DesignProduct?.[currentViewId];
      if (!dp || typeof dp === 'string') return [];

      const specs = Array.isArray(dp.allowDesign_index) ? dp.allowDesign_index : [];
      const set = new Set();
      const parseRange = (spec) => {
        const s = String(spec || '').trim();
        if (!s) return;
        if (s.includes('-')) {
          const [a, b] = s.split('-').map(n => parseInt(n.trim(), 10));
          if (isFinite(a) && isFinite(b) && a > 0 && b >= a) {
            for (let x = a; x <= b; x++) set.add(x);
          }
        } else {
          const v = parseInt(s, 10);
          if (isFinite(v) && v > 0) set.add(v);
        }
      };
      specs.forEach(parseRange);

      let arr = Array.from(set).sort((a, b) => a - b);
      const max = Number(typeof dp.maxDesignCount === 'number' ? dp.maxDesignCount : maxDesignCount);
      if (isFinite(max) && max > 0) {
        arr = arr.filter(n => n <= max);
      }
      return arr;
    } catch (_) {
      return [];
    }
  }, [configData, currentViewId, optionsVersion, maxDesignCount]);


  
  // Local state: design index per viewId
  const [designIndexByView, setDesignIndexByView] = useState({});
  // Prefer global per-view source of truth if present
  const currentDesignIndex = (() => {
    try {
      const dibv = (typeof window !== 'undefined' && window.designIndexByView) || null;
      const idx = Number(dibv && currentViewId != null ? dibv[currentViewId] : undefined);
      if (Number.isFinite(idx) && idx > 0) return idx;
    } catch (_) {}
    return Math.max(1, Number(designIndexByView[currentViewId] || 1));
  })();

  // When view or its max changes, clamp/reset the design index for that view (only by max, not by allowedIndices)
  React.useEffect(() => {
    // If current view got filtered out, switch to first available
    if (!currentViewId && total > 0) {
      console.log('[Header] currentViewId empty, switching to first enabled');
      setSelectedView(productViews[0]);
      return;
    }
    if (total > 0 && rawIndex === -1) {
      // Find the nearest enabled view BEFORE the old position in the original order
      const rawOrderIndex = (productViewsRaw || []).findIndex(v => getViewId(v) === currentViewId);
      console.log('[Header] Current view disabled by linked_view. currentViewId=', currentViewId, 'rawOrderIndex=', rawOrderIndex,
        'filteredViews=', productViews.map(getViewId), 'rawViews=', (productViewsRaw||[]).map(getViewId));
      let candidate = null;
      if (rawOrderIndex >= 0) {
        // Look backwards in original order for an enabled view
        for (let i = rawOrderIndex - 1; i >= 0; i--) {
          const viewIdAtI = getViewId(productViewsRaw[i]);
          const found = productViews.find(v => getViewId(v) === viewIdAtI);
          if (found) { candidate = found; break; }
        }
        // If none before, look forwards in original order for an enabled view
        if (!candidate) {
          for (let i = rawOrderIndex + 1; i < productViewsRaw.length; i++) {
            const viewIdAtI = getViewId(productViewsRaw[i]);
            const found = productViews.find(v => getViewId(v) === viewIdAtI);
            if (found) { candidate = found; break; }
          }
        }
      }
      // Fallback to first enabled
      candidate = candidate || productViews[0];
      console.log('[Header] Auto-switching to candidate view:', getViewId(candidate));
      if (candidate) setSelectedView(candidate);
    }
    setDesignIndexByView((prev) => {
      const curr = Math.max(1, Number(prev[currentViewId] || 1));

      const max = Number(maxDesignCount);
      if (!isFinite(max) || max <= 1) {
        if (curr !== 1) return { ...prev, [currentViewId]: 1 };
        return prev;
      }
      if (curr > max) return { ...prev, [currentViewId]: max };
      return prev;
    });
  }, [currentViewId, maxDesignCount, productViews, productViewsRaw]);

  // On view change, sync local header state. Default to smallest allowed index when no global index exists.
  React.useEffect(() => {
    try {
      if (!currentViewId) return;
      const dibv = (typeof window !== 'undefined' && window.designIndexByView) || {};
      const idxRaw = dibv[currentViewId] != null ? dibv[currentViewId] : (typeof window !== 'undefined' ? window.currentDesignIndex : null);
      const idxFromGlobal = Number(idxRaw);
      const minAllowed = (allowedIndices && allowedIndices.length > 0) ? allowedIndices[0] : 1;
      let nextIdx = Number.isFinite(idxFromGlobal) && idxFromGlobal > 0 ? idxFromGlobal : minAllowed;
      const max = Number(maxDesignCount);
      if (Number.isFinite(max) && max > 0) nextIdx = Math.min(nextIdx, max);
      nextIdx = Math.max(1, nextIdx);
      setDesignIndexByView((prev) => {
        const curr = Math.max(1, Number(prev[currentViewId] || 1));
        if (curr === nextIdx) return prev;
        return { ...prev, [currentViewId]: nextIdx };
      });
      // Sync to globals so other components (and future loads) match
      try {
        window.designIndexByView = window.designIndexByView || {};
        window.designIndexByView[currentViewId] = nextIdx;
        window.currentDesignIndex = nextIdx;
      } catch {}
    } catch {}
  }, [currentViewId, allowedIndices, maxDesignCount]);

  // Reset design index to smallest allowed when the active variant changes
  React.useEffect(() => {
    const minAllowed = (allowedIndices && allowedIndices.length > 0) ? allowedIndices[0] : 1;
    let nextIdx = minAllowed;
    const max = Number(maxDesignCount);
    if (Number.isFinite(max) && max > 0) nextIdx = Math.min(nextIdx, max);
    nextIdx = Math.max(1, nextIdx);
    setDesignIndexByView((prev) => ({ ...prev, [currentViewId]: nextIdx }));
    try {
      if (currentViewId) {
        window.designIndexByView = window.designIndexByView || {};
        window.designIndexByView[currentViewId] = nextIdx;
        window.currentDesignIndex = nextIdx;
      }
    } catch {}
  }, [activeVariantKey, currentViewId, allowedIndices, maxDesignCount]);

  // Build display label: always show "<ViewName> <index>"
  const displayLabel = `${getViewName(selectedView)} ${currentDesignIndex}`;

  // Determine if a specific theme is preselected via URL (themeid) and valid in config
  const hasLockedThemeFromUrl = useMemo(() => {
    try {
      const idx = getThemeIndexFromUrl();
      const themesLen = Array.isArray(configData?.Themes) ? configData.Themes.length : 0;
      return idx !== null && idx >= 0 && idx < themesLen;
    } catch (_) {
      return false;
    }
  }, [configData]);

  const ensureViewObject = (raw) => {
    if (!raw) return raw;
    if (typeof raw === 'object' && raw.id) return raw;
    const viewId = getViewId(raw);
    const match = productViews.find(v => getViewId(v) === viewId);
    if (match && typeof match === 'object') return match;
    return { id: viewId, name: viewId };
  };

  const goToViewIndex = (idx) => {
    if (idx < 0 || idx >= total) return;
    const nextViewRaw = productViews[idx];
    const nextView = ensureViewObject(nextViewRaw);
    if (getViewId(selectedView) === getViewId(nextView)) return;

    const ev = new CustomEvent('saveDesignBeforeViewChange', { detail: { newViewId: getViewId(nextView) } });
    window.dispatchEvent(ev);
    setSelectedView(nextView);
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      goToViewIndex(currentIndex - 1);
    } else if (configData?.Themes && configData.Themes.length > 0) {
      onShowThemes && onShowThemes();
    }
  };

  const [showDoneConfirm, setShowDoneConfirm] = useState(false);

  const handleNextOrDone = () => {
    if (currentIndex < total - 1) {
      goToViewIndex(currentIndex + 1);
    } else {
      // Open in-app confirmation modal instead of native confirm
      setShowDoneConfirm(true);
    }
  };

  const currentViewObj = ensureViewObject(productViews[currentIndex] || selectedView);

  // Do not render when no product or no views
  if (!selectedProduct || total === 0) return null;

  const disableBack = currentIndex === 0 && hasLockedThemeFromUrl;

  const headerMode = String(configData?.style?.HeaderMode || 'Free').toLowerCase();

  return (
    <div style={{
      // Normal flow header (no overlay)
      width: '100%',
      boxSizing: 'border-box',
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: '56px',
      marginBottom: '8px',
      gap: '12px'
    }}>
      {/* Left: Back (icon only). Hidden in Free mode */}
      <div style={{ minWidth: 60, display: 'flex' }}>
        {headerMode !== 'free' && (
          <button
            onClick={handleBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              background: 'var(--site-muted)',
              border: '1px solid var(--site-border)',
              color: 'var(--site-text)',
              borderRadius: '10px',
              cursor: disableBack ? 'not-allowed' : 'var(--cursor-pointer)',
              boxShadow: 'none',
              transition: 'background 600ms ease',
              opacity: disableBack ? 0.5 : 1
            }}
            disabled={disableBack}
            aria-label={t('general.back')}
            aria-disabled={disableBack}
            title={disableBack ? t('header.backDisabled') : t('general.back')}
            onMouseEnter={(e) => {
              if (!disableBack) {
                e.currentTarget.style.background = 'var(--site-hover-bg)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--site-muted)';
            }}
          >
            <BiArrowBack size={22} color="var(--site-text)" />
          </button>
        )}
      </div>

      {/* Center: View buttons or step indicator */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: headerMode === 'free' ? 'flex-start' : 'center',
        // In Free mode allow multi-line wrapping, so align content to top
        alignItems: headerMode === 'free' ? 'flex-start' : 'center',
        gap: 8
      }}>
        {showViewButtons ? (
          // In Free mode, when there are too many views, use a dropdown for better fit
          (() => {
            // Treat small screens as mobile; safe-guard for SSR/undefined window
            const isMobile = (() => {
              try {
                if (typeof window === 'undefined' || !window.matchMedia) return false;
                return window.matchMedia('(max-width: 768px)').matches;
              } catch (_) {
                return false;
              }
            })();
            const useDropdown = headerMode === 'free' && (productViews.length > 8 || isMobile);
            if (useDropdown) {
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}>
                  <select
                    aria-label={t('header.selectView', 'Select view')}
                    value={getViewId(selectedView)}
                    onChange={(e) => {
                      const id = e.target.value;
                      const next = ensureViewObject(productViews.find(v => getViewId(v) === id) || { id, name: id });
                      if (getViewId(selectedView) === id) return;
                      try {
                        const ev = new CustomEvent('saveDesignBeforeViewChange', { detail: { newViewId: id } });
                        window.dispatchEvent(ev);
                      } catch {}
                      setSelectedView(next);
                    }}
                    style={{
                      maxWidth: '100%',
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1px solid var(--site-border)',
                      background: 'var(--site-muted)',
                      color: 'var(--site-text)',
                      fontWeight: 600,
                    }}
                  >
                    {productViews.map((v) => (
                      <option key={getViewId(v)} value={getViewId(v)}>{getViewName(ensureViewObject(v))}</option>
                    ))}
                  </select>
                </div>
              );
            }
            return null;
          })() || (
          <div
            className="header-view-button-group"
            style={{
              display: 'inline-flex',
              gap: 0,
              alignItems: 'center',
              // In Free mode, wrap to multiple lines so all options are visible
              flexWrap: headerMode === 'free' ? 'wrap' : 'nowrap',
              // Keep horizontal scroll for non-free modes or when wrapping is unnecessary
              overflowX: headerMode === 'free' ? 'visible' : 'auto',
              transition: 'opacity 420ms ease, transform 320ms ease',
              // Override global styles from ViewSelection.css
              background: '#777',
              border: 'none',
              boxShadow: 'none',
              borderRadius: 22,
              padding: 2,
              justifyContent: 'center',
              marginLeft: 'auto',
              marginRight: 'auto',
              alignSelf: 'center'
            }}
          >
            {productViews.map((v) => {
              const id = getViewId(v);
              const label = getViewName(ensureViewObject(v));
              const active = getViewId(selectedView) === id;
              return (
                <button
                  key={id}
                  className={`header-view-button ${active ? 'active' : ''}`}
                  onClick={() => {
                    if (active) return;
                    const ev = new CustomEvent('saveDesignBeforeViewChange', { detail: { newViewId: id } });
                    window.dispatchEvent(ev);
                    setSelectedView(ensureViewObject(v));
                  }}
                >
                  {label}
                </button>
              );
            })}
            {/* dashed divider removed per request */}
          </div>
          )
        ) : (
          <div
            style={{
              color: 'var(--site-text)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1.15,
            }}
          >
            {(() => {
              const selectable = allowedIndices.length === 0 || allowedIndices.includes(currentDesignIndex);
              const manyChoices = allowedIndices.length > 1 || (allowedIndices.length === 0 && Number(maxDesignCount) > 1);
              return selectable && manyChoices;
            })() ? (
              <select
                aria-label={t('header.designIndex')}
                value={currentDesignIndex}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  const prevIdx = Math.max(1, Number(designIndexByView[currentViewId] || 1));
                  console.log(`[Header] Design index changing ${prevIdx} -> ${idx} for view ${currentViewId} (max=${maxDesignCount})`);
                  // Ask canvas to save current design under the PREVIOUS index before we switch
                  try {
                    window.dispatchEvent(
                      new CustomEvent('SAVE_DESIGN_BEFORE_INDEX_CHANGE', {
                        detail: { viewId: currentViewId, prevIndex: prevIdx, nextIndex: idx },
                      })
                    );
                  } catch {}
                  // Update local state
                  setDesignIndexByView((prev) => ({ ...prev, [currentViewId]: idx }));
                  // Persist to globals for controller to use in save/load keys
                  try {
                    window.designIndexByView = window.designIndexByView || {};
                    window.designIndexByView[currentViewId] = idx;
                    window.currentDesignIndex = idx;
                  } catch {}
                  try {
                    window.dispatchEvent(new CustomEvent('DESIGN_INDEX_CHANGED', { detail: { viewId: currentViewId, index: idx, prevIndex: prevIdx } }));
                    // Proactively request a design reset to mirror toolbar reset behavior
                    console.log('[Header] REQUEST_RESET_DESIGN due to design index change');
                    window.dispatchEvent(
                      new CustomEvent('REQUEST_RESET_DESIGN', {
                        detail: { reason: 'design-index-change', viewId: currentViewId, index: idx, prevIndex: prevIdx },
                      })
                    );
                  } catch {}
                }}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: '1px solid var(--site-border)',
                  background: 'var(--site-muted)',
                  color: 'var(--site-text)'
                }}
              >
                {(allowedIndices.length > 0
                  ? allowedIndices
                  : Array.from({ length: Number(maxDesignCount) }, (_, i) => i + 1)
                ).map((n) => (
                  <option key={n} value={n}>{`${getViewName(selectedView)} ${n}`}</option>
                ))}
              </select>
            ) : (
              <span style={{
                fontSize: 14,
                fontWeight: 700,
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {displayLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Right: Next/Done. In Free mode show only Done; otherwise Next/Done */}
      <div style={{ minWidth: 60, display: 'flex', justifyContent: 'flex-end' }}>
        {headerMode === 'free' ? (
          <button
            onClick={() => (onDone ? onDone() : window.dispatchEvent(new CustomEvent('viewSelectionDone')))}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              background: 'var(--site-muted)',
              border: '1px solid var(--site-border)',
              color: 'var(--site-text)',
              borderRadius: '10px',
              cursor: 'var(--cursor-pointer)',
              boxShadow: 'none',
              transition: 'background 600ms ease, transform 320ms ease'
            }}
            aria-label={t('general.done')}
            title={t('general.done')}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--site-hover-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--site-muted)'; }}
          >
            <BiCheck style={{ transition: 'opacity 420ms ease, transform 420ms ease', opacity: 1 }} size={22} color="var(--site-text)" />
          </button>
        ) : (
          <button
            onClick={handleNextOrDone}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              background: 'var(--site-muted)',
              border: '1px solid var(--site-border)',
              color: 'var(--site-text)',
              borderRadius: '10px',
              cursor: 'var(--cursor-pointer)',
              boxShadow: 'none',
              transition: 'background 600ms ease, transform 320ms ease'
            }}
            aria-label={currentIndex < total - 1 ? t('general.next') : t('general.done')}
            title={currentIndex < total - 1 ? t('general.next') : t('general.done')}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--site-hover-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--site-muted)'; }}
          >
            {currentIndex < total - 1 ? (
              <BiArrowBack style={{ transform: 'rotate(180deg)', transition: 'opacity 420ms ease, transform 420ms ease', opacity: 1 }} size={22} color="var(--site-text)" />
            ) : (
              <BiCheck style={{ transition: 'opacity 420ms ease, transform 420ms ease', opacity: 1 }} size={22} color="var(--site-text)" />
            )}
          </button>
        )}
      </div>

      {/* In-app confirmation modal */}
      {showDoneConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowDoneConfirm(false)}
        >
          <div
            style={{
              background: 'var(--site-bg)',
              color: 'var(--site-text)',
              border: '1px solid var(--site-border)',
              borderRadius: 12,
              padding: 16,
              minWidth: 280,
              boxShadow: '0 6px 24px rgba(0,0,0,0.18)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('header.confirmExportTitle')}</div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>{t('header.confirmExportMessage')}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDoneConfirm(false)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--site-border)',
                  background: 'var(--site-muted)',
                  color: 'var(--site-text)'
                }}
              >
                {t('general.cancel')}
              </button>
              <button
                onClick={() => {
                  setShowDoneConfirm(false);
                  onDone ? onDone() : window.dispatchEvent(new CustomEvent('viewSelectionDone'));
                }}
                className="confirm-primary"
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--site-border)',
                  background: 'var(--site-accent, #2b7cff)',
                  color: 'white',
                  fontWeight: 700
                }}
              >
                {t('general.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderBar;
